/**
 * @file /src/lib/utils/fit/knockouts.ts
 *
 * @description
 * Knockout requirements: pass/fail conditions of a job (location, on-site or remote, work authorization, citizenship,
 * salary range, clearance) that experience cannot show and that the candidate's profile and autofill data can answer.
 * They are never part of the keyword or competency scores. Each one is Clear, Conflict or Unknown; a Conflict on a
 * requirement the job states as hard caps the overall status at red ("may not qualify" in the UI) and leaves the scores alone.
 * Salary and location are the exceptions to the decision model: they are compared in code (the user's minimum against the
 * job's stated range; the distance between the candidate and the job's city), so they are exact and testable.
 * Everything else is judged by the model from the candidate's facts.
 * Pure helpers only (no AI, network or Firestore); the decision model call lives in resumeMatchScoring.ts.
 */

import { JEV_MODEL, MAX_JD_CHARS } from "@/lib/utils/document/keywordMatch";
import type { JdRequirement, JdSalaryRange, KnockoutKind, KnockoutMatch, KnockoutStatus, SalaryPeriod } from "@/types";

/** The decision model takes a limited number of questions per request; longer jobs are split across requests. */
export const MAX_QUESTIONS_PER_REQUEST = 40;

// Backstop for the extractor's knockout category: schedule and place requirements cannot be evidenced from a
// profile and the user cannot fix them with an answer about their experience. Conservative on purpose: "remote
// teams" or "travel industry" are real experience requirements and must still be scored as competencies, and so is
// "hybrid roles" (a kind of sales role, not a way of working).
const KNOCKOUT_TEXT =
  /\b(\d+\s+days?\s+(a|per)\s+week|on-?site|in[- ]office|hybrid(?!\s+(cloud|it|infrastructure|environments?|learning|events?|apps?|model|roles))|relocat\w+|work from (home|the office))\b/i;

export function isKnockoutText(text: string): boolean {
  return KNOCKOUT_TEXT.test(text);
}

type KnockoutLike = Pick<JdRequirement, "requirement" | "category"> & { terms?: string[] };

/**
 * "logistics" is the category older saved analyses used for what are now knockouts. The text backstop only applies to a
 * requirement with no keywords: a condition like "on-site 3 days a week" names no skill or tool, while a responsibility
 * that happens to mention a trigger word ("training for setters, closers and hybrid roles") does.
 */
export function isKnockoutRequirement(r: KnockoutLike): boolean {
  return (
    r.category === "knockout" ||
    r.category === "logistics" ||
    ((r.terms ?? []).length === 0 && isKnockoutText(r.requirement))
  );
}

/** The kind and strictness of a knockout. Ones found only by the text backstop are soft arrangement conditions. */
export function knockoutMeta(r: Pick<JdRequirement, "knockout">): { kind: KnockoutKind; hard: boolean } {
  return { kind: r.knockout?.kind ?? "arrangement", hard: r.knockout?.hard ?? false };
}

// What the candidate's profile and autofill data can answer. A schedule ("Monday to Friday") or another condition
// ("background check") has no fact to check it against and the user has no field to fill in, so it is not shown as a
// must-have: it would sit in the list as "not in your profile" forever.
const CHECKABLE_KINDS: ReadonlySet<KnockoutKind> = new Set([
  "location",
  "arrangement",
  "authorization",
  "citizenship",
  "salary",
  "clearance",
]);

export function isCheckableKnockout(r: Pick<JdRequirement, "knockout">): boolean {
  return CHECKABLE_KINDS.has(knockoutMeta(r).kind);
}

const CRITERIA: Record<KnockoutStatus, string> = {
  clear: "The candidate facts show the candidate meets the requirement.",
  conflict:
    "The candidate facts show the candidate does not meet it. Examples: the job requires US citizenship and the candidate's citizenships do not include it; the job requires no visa sponsorship and the candidate needs sponsorship; the job is on-site and the candidate wants to work remotely only. A different city nearby is not a conflict.",
  unknown:
    "The facts do not say, a needed fact is marked not provided, or the requirement cannot be decided from these facts. Use unknown rather than guessing.",
};

type JevRequest = { model: string; state: Record<string, string>; questions: Record<string, unknown> };

/**
 * Requests for the decision model: one Choice question per knockout (keys k0, k1, ...), split into chunks of
 * MAX_QUESTIONS_PER_REQUEST. The state holds only the non-demographic candidate facts and the job description.
 */
export function buildKnockoutRequests(args: {
  factsText: string;
  jobDescriptionText: string;
  requirements: Pick<JdRequirement, "requirement">[];
}): JevRequest[] {
  const state = {
    candidate_facts: args.factsText,
    job_description: args.jobDescriptionText.slice(0, MAX_JD_CHARS),
  };
  const requests: JevRequest[] = [];
  for (let i = 0; i < args.requirements.length; i += MAX_QUESTIONS_PER_REQUEST) {
    const questions: Record<string, unknown> = {};
    args.requirements.slice(i, i + MAX_QUESTIONS_PER_REQUEST).forEach((r, j) => {
      questions[`k${i + j}`] = {
        type: "choice",
        instructions: `Based only on the candidate facts, does the candidate meet this job requirement: "${r.requirement}"?`,
        criteria: CRITERIA,
      };
    });
    requests.push({ model: JEV_MODEL, state, questions });
  }
  return requests;
}

/** Maps the merged answers onto the knockouts. A missing or unrecognized answer counts as unknown. */
export function parseKnockoutAnswers(
  body: { answers?: Record<string, unknown> },
  requirements: Pick<JdRequirement, "id" | "requirement" | "knockout">[],
): KnockoutMatch[] {
  const answers = (body.answers ?? {}) as Record<string, { choice?: string }>;
  return requirements.map((r, i) => {
    const choice = answers[`k${i}`]?.choice;
    const status: KnockoutStatus = choice === "clear" || choice === "conflict" ? choice : "unknown";
    return { requirementId: r.id, requirement: r.requirement, ...knockoutMeta(r), status };
  });
}

/** A job this close to the candidate counts as a commute. Farther than this, the candidate would have to relocate. */
export const COMMUTE_RADIUS_MILES = 30;

/** Location knockouts are decided in code, from the distance between two cities. */
export const isLocationKnockout = (r: Pick<JdRequirement, "knockout">): boolean => r.knockout?.kind === "location";

export interface LatLon {
  lat: number;
  lon: number;
}

/** Great-circle distance in miles (haversine). */
export function milesBetween(a: LatLon, b: LatLon): number {
  const R = 3958.8;
  const rad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export interface LocationOutcome {
  status: KnockoutStatus;
  needs?: "relocation";
}

/**
 * Whether the candidate can take a job in a place, from the miles between them (null when either place could not be
 * found). Within the commute radius it is met. Farther away it depends on whether they would relocate: yes is met, no
 * is a conflict, and no answer yet is unknown, and asking the relocation question would settle it.
 */
export function locationStatus(miles: number | null, willRelocate: "yes" | "no" | "" | undefined): LocationOutcome {
  if (miles === null) return { status: "unknown" };
  if (miles <= COMMUTE_RADIUS_MILES) return { status: "clear" };
  if (willRelocate === "yes") return { status: "clear" };
  if (willRelocate === "no") return { status: "conflict" };
  return { status: "unknown", needs: "relocation" };
}

/** The match for a location knockout. Whether it is hard comes from the job: only on-site wording makes it required. */
export function locationKnockoutMatch(
  r: Pick<JdRequirement, "id" | "requirement" | "knockout">,
  outcome: LocationOutcome,
): KnockoutMatch {
  return { requirementId: r.id, requirement: r.requirement, ...knockoutMeta(r), ...outcome };
}

const PERIODS_PER_YEAR: Record<SalaryPeriod, number> = { year: 1, month: 12, hour: 2080 };

/** Salary knockouts are decided in code, not by the model. */
export const isSalaryKnockout = (r: Pick<JdRequirement, "knockout">): boolean => r.knockout?.kind === "salary";

/**
 * The user's minimum pay against the job's stated range, both converted to a yearly amount. A conflict only when the
 * top of the range is below the user's minimum: an open-ended range ("from $100k") or a minimum at or below the top is
 * met. Unknown when either side is missing or the currencies differ (no exchange rates are used).
 */
export function salaryStatus(
  user: { min?: number | null; currency?: string; period?: SalaryPeriod },
  job: JdSalaryRange | undefined,
): KnockoutStatus {
  if (!job || (job.min === undefined && job.max === undefined)) return "unknown";
  if (!user.min || user.min <= 0) return "unknown";
  if ((user.currency || "USD").toUpperCase() !== job.currency.toUpperCase()) return "unknown";
  const userYearly = user.min * PERIODS_PER_YEAR[user.period ?? "year"];
  if (job.max !== undefined && job.max * PERIODS_PER_YEAR[job.period] < userYearly) return "conflict";
  return "clear";
}

/** The match for a salary knockout. A stated salary range is informational, so it is never a hard requirement. */
export function salaryKnockoutMatch(
  r: Pick<JdRequirement, "id" | "requirement" | "knockout">,
  user: Parameters<typeof salaryStatus>[0],
): KnockoutMatch {
  return {
    requirementId: r.id,
    requirement: r.requirement,
    ...knockoutMeta(r),
    hard: false,
    status: salaryStatus(user, r.knockout?.salary),
    ...(r.knockout?.salary ? { salary: r.knockout.salary } : {}),
  };
}

export function summarizeKnockouts(knockouts: Pick<KnockoutMatch, "status" | "hard">[]) {
  const count = (s: KnockoutStatus) => knockouts.filter((k) => k.status === s).length;
  return {
    total: knockouts.length,
    clear: count("clear"),
    conflict: count("conflict"),
    unknown: count("unknown"),
    screenedOut: knockouts.some((k) => k.status === "conflict" && k.hard),
  };
}

/** Attaches the knockouts to a report. A hard conflict caps the status at red; the numeric scores are never changed. */
export function applyKnockouts<T extends { overallStatus: "red" | "yellow" | "green" }>(
  report: T,
  knockouts: KnockoutMatch[],
): T & { knockouts: KnockoutMatch[]; screenedOut: boolean } {
  const { screenedOut } = summarizeKnockouts(knockouts);
  return { ...report, knockouts, screenedOut, overallStatus: screenedOut ? "red" : report.overallStatus };
}
